import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add,
  Image as ImageIcon,
  Link as LinkIcon,
  Delete,
  Edit,
  Visibility,
  VisibilityOff,
  CloudUpload,
  TextFields,
  Wallpaper,
  School,
  ShoppingBag,
} from "@mui/icons-material";
import { bannersAPI, Banner, BannerPayload } from "../../services/adminApi";

const MAX_BANNERS = 3;

const COLORS = {
  accent: "#1565C0",
  text: "#0A1628",
  border: "rgba(0,0,0,0.08)",
  bg: "#F0F7FF",
};

const DEFAULT_FORM = (page: "classes" | "shop"): BannerPayload => ({
  title: "",
  subtitle: "",
  imageUrl: "",
  cfImageId: "",
  linkUrl: "",
  linkLabel: "",
  type: "text",
  bgColor: "#1565C0",
  textColor: "#FFFFFF",
  active: true,
  page,
});

// â”€â”€â”€ Mini live preview â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const BannerPreview: React.FC<{ form: BannerPayload }> = ({ form }) => (
  <Box
    sx={{
      borderRadius: "12px",
      overflow: "hidden",
      border: `1px solid ${COLORS.border}`,
      height: 140,
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ...(form.type === "image" && form.imageUrl
        ? {
            backgroundImage: `url(${form.imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }
        : { bgcolor: form.bgColor || "#1565C0" }),
    }}
  >
    {form.type === "image" && form.imageUrl && (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 60%, transparent)",
        }}
      />
    )}
    <Box sx={{ position: "relative", zIndex: 1, px: 3, textAlign: "center" }}>
      <Typography
        variant="h6"
        fontWeight={800}
        sx={{ color: form.textColor || "#FFFFFF", fontSize: "1rem", lineHeight: 1.3 }}
      >
        {form.title || "Banner title"}
      </Typography>
      {form.subtitle && (
        <Typography
          variant="caption"
          sx={{ color: form.textColor || "#FFFFFF", opacity: 0.85, display: "block", mt: 0.4 }}
        >
          {form.subtitle}
        </Typography>
      )}
      {form.linkLabel && (
        <Chip
          label={form.linkLabel}
          size="small"
          sx={{
            mt: 0.8,
            bgcolor: "rgba(255,255,255,0.25)",
            color: form.textColor || "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.68rem",
            height: 22,
          }}
        />
      )}
    </Box>
  </Box>
);

// â”€â”€â”€ Banner card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const BannerCard: React.FC<{
  banner: Banner;
  onEdit: (b: Banner) => void;
  onDelete: (id: string) => void;
  onToggleActive: (b: Banner) => void;
}> = ({ banner, onEdit, onDelete, onToggleActive }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: "14px",
      border: `1px solid ${COLORS.border}`,
      overflow: "hidden",
      opacity: banner.active ? 1 : 0.55,
      transition: "opacity 0.2s",
    }}
  >
    {/* Preview strip */}
    <Box
      sx={{
        height: 110,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(banner.type === "image" && banner.imageUrl
          ? {
              backgroundImage: `url(${banner.imageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : { bgcolor: banner.bgColor || "#1565C0" }),
      }}
    >
      {banner.type === "image" && banner.imageUrl && (
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(0,0,0,0.5), transparent)" }} />
      )}
      <Box sx={{ position: "relative", zIndex: 1, px: 2, textAlign: "center" }}>
        <Typography fontWeight={800} sx={{ color: banner.textColor || "#FFF", fontSize: "0.9rem" }}>
          {banner.title}
        </Typography>
        {banner.subtitle && (
          <Typography variant="caption" sx={{ color: banner.textColor || "#FFF", opacity: 0.85 }}>
            {banner.subtitle}
          </Typography>
        )}
      </Box>
      {/* Badges */}
      <Box sx={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 0.5 }}>
        <Chip
          size="small"
          label={banner.type === "image" ? "Image" : "Text"}
          icon={banner.type === "image" ? <Wallpaper sx={{ fontSize: "12px !important" }} /> : <TextFields sx={{ fontSize: "12px !important" }} />}
          sx={{ height: 20, fontSize: "0.62rem", bgcolor: "rgba(0,0,0,0.45)", color: "#FFF" }}
        />
        {!banner.active && (
          <Chip size="small" label="Hidden" sx={{ height: 20, fontSize: "0.62rem", bgcolor: "rgba(0,0,0,0.55)", color: "#FFF" }} />
        )}
      </Box>
      <Box sx={{ position: "absolute", top: 6, right: 6 }}>
        <Chip
          size="small"
          label={`#${banner.order}`}
          sx={{ height: 20, fontSize: "0.62rem", bgcolor: "rgba(0,0,0,0.4)", color: "#FFF", fontWeight: 700 }}
        />
      </Box>
    </Box>

    {/* Actions */}
    <Box sx={{ px: 2, py: 1.2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {banner.linkUrl && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.72rem" }}>
            ðŸ”— {banner.linkLabel || banner.linkUrl}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 0.3 }}>
        <Tooltip title={banner.active ? "Hide banner" : "Show banner"} arrow>
          <IconButton size="small" onClick={() => onToggleActive(banner)} sx={{ color: banner.active ? "#10B981" : "#9CA3AF" }}>
            {banner.active ? <Visibility sx={{ fontSize: 17 }} /> : <VisibilityOff sx={{ fontSize: 17 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit" arrow>
          <IconButton size="small" onClick={() => onEdit(banner)} sx={{ color: COLORS.accent }}>
            <Edit sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" arrow>
          <IconButton size="small" onClick={() => onDelete(banner._id)} sx={{ color: "#EF4444", opacity: 0.7, "&:hover": { opacity: 1 } }}>
            <Delete sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  </Paper>
);

// â”€â”€â”€ Main page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const AdminBanners: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Active page tab
  const [activePage, setActivePage] = useState<"classes" | "shop">("classes");

  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Form dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BannerPayload>({ ...DEFAULT_FORM("classes") });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Image upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Delete
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async (page: "classes" | "shop" = activePage) => {
    try {
      setLoading(true);
      setError("");
      const res = await bannersAPI.list(page);
      setBanners(res.data.banners || []);
    } catch {
      setError("Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(activePage); }, [activePage]); // eslint-disable-line

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...DEFAULT_FORM(activePage), order: banners.length + 1 });
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (b: Banner) => {
    setEditingId(b._id);
    setForm({
      title: b.title,
      subtitle: b.subtitle ?? "",
      imageUrl: b.imageUrl ?? "",
      cfImageId: b.cfImageId ?? "",
      linkUrl: b.linkUrl ?? "",
      linkLabel: b.linkLabel ?? "",
      type: b.type,
      bgColor: b.bgColor ?? "#1565C0",
      textColor: b.textColor ?? "#FFFFFF",
      order: b.order,
      active: b.active,
      page: b.page,
    });
    setFormError("");
    setDialogOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await bannersAPI.uploadImage(file);
      setForm((f) => ({ ...f, imageUrl: res.data.url, cfImageId: res.data.cfImageId ?? "", type: "image" }));
    } catch {
      setFormError("Image upload failed. Try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setFormError("Title is required");
      return;
    }
    if (form.type === "image" && !form.imageUrl) {
      setFormError("Upload an image or switch to Text type");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      if (editingId) {
        const res = await bannersAPI.update(editingId, form);
        setBanners((prev) => prev.map((b) => (b._id === editingId ? res.data.banner : b)));
      } else {
        const res = await bannersAPI.create(form);
        setBanners((prev) => [...prev, res.data.banner].sort((a, b) => a.order - b.order));
      }
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (b: Banner) => {
    try {
      const res = await bannersAPI.update(b._id, { active: !b.active });
      setBanners((prev) => prev.map((x) => (x._id === b._id ? res.data.banner : x)));
    } catch {
      setError("Failed to update banner");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await bannersAPI.remove(deleteId);
      setBanners((prev) => prev.filter((b) => b._id !== deleteId));
      setDeleteId(null);
    } catch {
      setError("Failed to delete banner");
    } finally {
      setDeleting(false);
    }
  };

  const canCreate = banners.length < MAX_BANNERS;
  const pageLabel = activePage === "classes" ? "Classes" : "Shop";

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: COLORS.text }}>
            Banners
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Up to {MAX_BANNERS} banners per page, shown above the content
          </Typography>
        </Box>
        <Tooltip title={!canCreate ? `Maximum ${MAX_BANNERS} banners reached for this page` : ""} arrow>
          <span>
            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={!canCreate}
              onClick={openCreate}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 700,
                bgcolor: COLORS.accent,
                "&:hover": { bgcolor: "#C8875E" },
              }}
            >
              Add Banner
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Page tabs */}
      <Tabs
        value={activePage}
        onChange={(_, v) => setActivePage(v)}
        sx={{
          mb: 2.5,
          "& .MuiTabs-indicator": { bgcolor: COLORS.accent },
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 40 },
          "& .Mui-selected": { color: `${COLORS.accent} !important` },
        }}
      >
        <Tab
          value="classes"
          label="Classes page"
          icon={<School sx={{ fontSize: 18 }} />}
          iconPosition="start"
        />
        <Tab
          value="shop"
          label="Shop page"
          icon={<ShoppingBag sx={{ fontSize: 18 }} />}
          iconPosition="start"
        />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }} onClose={() => setError("")}>{error}</Alert>}

      {/* Slot indicator */}
      <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
        {[1, 2, 3].map((n) => {
          const filled = banners.some((b) => b.order === n) || (n <= banners.length);
          return (
            <Chip
              key={n}
              label={`Slot ${n}: ${filled ? "Filled" : "Empty"}`}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: filled ? "rgba(216,154,122,0.16)" : "rgba(0,0,0,0.05)",
                color: filled ? COLORS.accent : "#9CA3AF",
              }}
            />
          );
        })}
      </Box>

      {/* Banner cards */}
      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "14px" }} />
            </Grid>
          ))}
        </Grid>
      ) : banners.length === 0 ? (
        <Paper
          elevation={0}
          onClick={openCreate}
          sx={{
            p: 6, borderRadius: "14px", border: `2px dashed ${COLORS.border}`,
            textAlign: "center", cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": { borderColor: COLORS.accent, bgcolor: "rgba(216,154,122,0.04)" },
          }}
        >
          <Wallpaper sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography variant="h6" gutterBottom color="text.secondary">No {pageLabel} banners yet</Typography>
          <Typography variant="body2" color="text.secondary">Click to add the first banner for this page</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {[...banners].sort((a, b) => a.order - b.order).map((banner) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={banner._id}>
              <BannerCard
                banner={banner}
                onEdit={openEdit}
                onDelete={setDeleteId}
                onToggleActive={handleToggleActive}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* â”€â”€â”€ Create / Edit Dialog â”€â”€â”€ */}
      <Dialog
        open={dialogOpen}
        onClose={() => !saving && !uploading && setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          {editingId ? "Edit Banner" : "New Banner"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2.2} sx={{ mt: 0.5 }}>
            {formError && (
              <Alert severity="error" sx={{ borderRadius: "10px" }}>{formError}</Alert>
            )}

            {/* Live preview */}
            <BannerPreview form={form} />

            {/* Type */}
            <FormControl fullWidth size="small">
              <InputLabel>Banner Type</InputLabel>
              <Select
                value={form.type}
                label="Banner Type"
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as "image" | "text" }))}
                sx={{ borderRadius: "10px" }}
              >
                <MenuItem value="text">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextFields sx={{ fontSize: 16 }} /> Text banner (color background)
                  </Box>
                </MenuItem>
                <MenuItem value="image">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ImageIcon sx={{ fontSize: 16 }} /> Image banner
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Image upload (only for image type) */}
            {form.type === "image" && (
              <Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ borderRadius: "10px", textTransform: "none", borderColor: COLORS.border }}
                >
                  {uploading ? "Uploadingâ€¦" : form.imageUrl ? "Replace Image" : "Upload Image"}
                </Button>
                {form.imageUrl && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Image set âœ“
                  </Typography>
                )}
              </Box>
            )}

            {/* Title */}
            <TextField
              fullWidth
              size="small"
              label="Title *"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              inputProps={{ maxLength: 200 }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            {/* Subtitle */}
            <TextField
              fullWidth
              size="small"
              label="Subtitle (optional)"
              value={form.subtitle}
              onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
              multiline
              rows={2}
              inputProps={{ maxLength: 400 }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            {/* Colors */}
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Background Color"
                  value={form.bgColor}
                  onChange={(e) => setForm((f) => ({ ...f, bgColor: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={form.bgColor}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setForm((f) => ({ ...f, bgColor: e.target.value }))
                          }
                          sx={{ width: 22, height: 22, border: "none", borderRadius: "4px", cursor: "pointer", p: 0 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Text Color"
                  value={form.textColor}
                  onChange={(e) => setForm((f) => ({ ...f, textColor: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={form.textColor}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setForm((f) => ({ ...f, textColor: e.target.value }))
                          }
                          sx={{ width: 22, height: 22, border: "none", borderRadius: "4px", cursor: "pointer", p: 0 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>
            </Grid>

            <Divider />

            {/* CTA Link */}
            <TextField
              fullWidth
              size="small"
              label="Link URL (optional)"
              placeholder="https://..."
              value={form.linkUrl}
              onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Link Button Label (optional)"
              placeholder="e.g. Shop Now"
              value={form.linkLabel}
              onChange={(e) => setForm((f) => ({ ...f, linkLabel: e.target.value }))}
              inputProps={{ maxLength: 60 }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            <Divider />

            {/* Order + Active */}
            <Grid container spacing={1.5} alignItems="center">
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Display Order"
                  value={form.order ?? 1}
                  onChange={(e) => setForm((f) => ({ ...f, order: Math.min(MAX_BANNERS, Math.max(1, Number(e.target.value))) }))}
                  inputProps={{ min: 1, max: MAX_BANNERS }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.active ?? true}
                      onChange={(_, v) => setForm((f) => ({ ...f, active: v }))}
                      sx={{ "& .MuiSwitch-thumb": { bgcolor: form.active ? COLORS.accent : undefined } }}
                    />
                  }
                  label={<Typography variant="body2" fontWeight={600}>{form.active ? "Visible" : "Hidden"}</Typography>}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving || uploading}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || uploading}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
              bgcolor: COLORS.accent,
              "&:hover": { bgcolor: "#C8875E" },
              minWidth: 100,
            }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: "#FFF" }} /> : editingId ? "Save Changes" : "Create Banner"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* â”€â”€â”€ Delete Confirm â”€â”€â”€ */}
      <Dialog
        open={!!deleteId}
        onClose={() => !deleting && setDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle fontWeight={700}>Delete Banner</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure? This banner will be permanently removed from the site.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleting} sx={{ textTransform: "none", borderRadius: "8px" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: "none", borderRadius: "8px", bgcolor: "#EF4444", "&:hover": { bgcolor: "#DC2626" }, minWidth: 90 }}
          >
            {deleting ? "Deletingâ€¦" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminBanners;


